import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StammdatenService } from '../../../services/stammdaten.service';
import { KontrollterminService } from '../../../services/kontrolltermin.service';
import { MultiSelectItem } from '../../../shared/interfaces/multi-select.interface';
import { getSession } from '../../../auth/storage';

export interface IndikatorEditData {
  indikator: any;
  befallAmpelOptions: any[];
  currentStatus?: string; // Add current kontrolltermin status
  isViewMode?: boolean; // Add view mode flag
}

@Component({
  selector: 'app-dialog-indikator-edit',
  templateUrl: './dialog-indikator-edit.component.html',
  styleUrls: ['./dialog-indikator-edit.component.css']
})
export class DialogIndikatorEditComponent implements OnInit {
  indikatorForm: FormGroup;
  loading = false;
  
  // View/Edit mode properties
  isViewMode = false;
  isEditMode = false;

  // Multi-select data
  massnahmenItems: MultiSelectItem[] = [];
  abweichungenItems: MultiSelectItem[] = [];
  artikelItems: MultiSelectItem[] = [];
  
  selectedMassnahmen: MultiSelectItem[] = [];
  selectedAbweichungen: MultiSelectItem[] = [];
  selectedArtikel: MultiSelectItem[] = [];

  massnahmenLoading = false;
  abweichungenLoading = false;
  artikelLoading = false;

  // Computed status based on wert vs schwellenwert
  computedStatusColor = '#6b7280';
  computedStatusId: '1' | '2' | '3' | '' = '';

  // Preview information
  previewInfo = {
    willBeCheckedBy: '',
    willBeCheckedAt: '',
    isFirstSave: false
  };

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DialogIndikatorEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IndikatorEditData,
    private stammdatenService: StammdatenService,
    private kontrollterminService: KontrollterminService
  ) {
    this.indikatorForm = this.fb.group({
      wert: [this.data.indikator.wert ?? 0, [Validators.required]],
      problem: [''], // Will be set from selectedAbweichungen
      massnahme: [''], // Will be set from selectedMassnahmen  
      interne_notiz: [''], // Will be set from selectedArtikel
      anmerkung: [this.data.indikator.anmerkung || '']
    });
  }

  ngOnInit(): void {
    console.log('🔧 Dialog opened for indikator:', this.data.indikator);
    console.log('🔧 Current status:', this.data.currentStatus);
    console.log('🔧 Is view mode:', this.data.isViewMode);
    
    // Set initial mode based on data
    this.isViewMode = this.data.isViewMode || this.data.currentStatus === 'freigabe' || this.data.currentStatus === 'abgeschlossen';
    this.isEditMode = !this.isViewMode;
    
    console.log('🔧 Final mode - View:', this.isViewMode, 'Edit:', this.isEditMode);
    
    // Update preview information
    this.updatePreviewInfo();
    
    this.loadMultiSelectData();
    this.parseExistingSelections();
    // Initialize and reactively compute status color
    this.updateComputedStatus(this.indikatorForm.get('wert')?.value);
    this.indikatorForm.get('wert')?.valueChanges.subscribe((v) => this.updateComputedStatus(v));
  }

  private getSchwellenwert(): number {
    const sw = this.data.indikator?.indikator?.schwellenwert;
    const num = Number(sw);
    return isFinite(num) ? num : 0;
  }

  private updateComputedStatus(rawValue: any): void {
    const wert = Number(rawValue);
    const schwellenwert = this.getSchwellenwert();
    if (!isFinite(wert)) {
      this.computedStatusId = '';
      this.computedStatusColor = '#6b7280';
      return;
    }
    if (wert === 0) {
      this.computedStatusId = '3'; // green / OK
      this.computedStatusColor = this.getBefallStatusColor('3');
    } else if (wert > schwellenwert) {
      this.computedStatusId = '1'; // red / critical
      this.computedStatusColor = this.getBefallStatusColor('1');
    } else {
      this.computedStatusId = '2'; // orange / warning
      this.computedStatusColor = this.getBefallStatusColor('2');
    }
  }

  getIndikatorDisplayNumber(): string {
    const n = this.data.indikator?.indikator?.nummer
      ?? this.data.indikator?.indikator_nummer
      ?? this.data.indikator?.nummer
      ?? this.data.indikator?.indikator_id;
    const num = Number(n);
    if (isFinite(num)) {
      return '#' + num.toString().padStart(3, '0');
    }
    return '#—';
  }

  private loadMultiSelectData(): void {
    // Load Massnahmen
    this.massnahmenLoading = true;
    this.stammdatenService.getMassnahmen().subscribe({
      next: (massnahmen) => {
        this.massnahmenItems = massnahmen.map(m => ({
          id: m.id.toString(),
          name: m.beschreibung,
          description: `Sortierung: ${m.sortierung}`
        }));
        this.massnahmenLoading = false;
      },
      error: (error) => {
        console.error('Error loading massnahmen:', error);
        this.massnahmenLoading = false;
      }
    });

    // Load Abweichungen
    this.abweichungenLoading = true;
    this.stammdatenService.getAbweichungen().subscribe({
      next: (abweichungen) => {
        this.abweichungenItems = abweichungen.map(a => ({
          id: a.id.toString(),
          name: a.caption,
          description: a.description
        }));
        this.abweichungenLoading = false;
      },
      error: (error) => {
        console.error('Error loading abweichungen:', error);
        this.abweichungenLoading = false;
      }
    });

    // Load Artikel
    this.artikelLoading = true;
    this.stammdatenService.getArtikels().subscribe({
      next: (artikel) => {
        this.artikelItems = artikel.map(a => ({
          id: a.id.toString(),
          name: a.beschreibung,
          description: `Nummer: ${a.nummer}`
        }));
        this.artikelLoading = false;
      },
      error: (error) => {
        console.error('Error loading artikel:', error);
        this.artikelLoading = false;
      }
    });
  }

  private parseExistingSelections(): void {
    // Parse existing JSON data from the indikator
    try {
      if (this.data.indikator.massnahme) {
        let massnahmenData;
        if (typeof this.data.indikator.massnahme === 'string') {
          massnahmenData = JSON.parse(this.data.indikator.massnahme);
        } else if (Array.isArray(this.data.indikator.massnahme)) {
          massnahmenData = this.data.indikator.massnahme;
        }
        
        if (Array.isArray(massnahmenData)) {
        this.selectedMassnahmen = massnahmenData.map((m: any) => ({
          id: m.id,
          name: m.name,
          description: m.description
        }));
        }
      }
    } catch (e) {
      console.log('No existing massnahmen data or invalid JSON:', e);
      this.selectedMassnahmen = [];
    }

    try {
      if (this.data.indikator.problem) {
        let abweichungenData;
        if (typeof this.data.indikator.problem === 'string') {
          abweichungenData = JSON.parse(this.data.indikator.problem);
        } else if (Array.isArray(this.data.indikator.problem)) {
          abweichungenData = this.data.indikator.problem;
        }
        
        if (Array.isArray(abweichungenData)) {
        this.selectedAbweichungen = abweichungenData.map((a: any) => ({
          id: a.id,
          name: a.name,
          description: a.description
        }));
        }
      }
    } catch (e) {
      console.log('No existing abweichungen data or invalid JSON:', e);
      this.selectedAbweichungen = [];
    }

    try {
      if (this.data.indikator.interne_notiz) {
        let artikelData;
        if (typeof this.data.indikator.interne_notiz === 'string') {
          artikelData = JSON.parse(this.data.indikator.interne_notiz);
        } else if (Array.isArray(this.data.indikator.interne_notiz)) {
          artikelData = this.data.indikator.interne_notiz;
        }
        
        if (Array.isArray(artikelData)) {
        this.selectedArtikel = artikelData.map((a: any) => ({
          id: a.id,
          name: a.name,
          description: a.description
        }));
        }
      }
    } catch (e) {
      console.log('No existing artikel data or invalid JSON:', e);
      this.selectedArtikel = [];
    }
  }

  /**
   * Switch from view mode to edit mode
   */
  switchToEditMode(): void {
    this.isViewMode = false;
    this.isEditMode = true;
    console.log('🔧 Switched to edit mode');
  }

  /**
   * Switch from edit mode to view mode
   */
  switchToViewMode(): void {
    this.isViewMode = true;
    this.isEditMode = false;
    console.log('🔧 Switched to view mode');
  }

  /**
   * Close dialog without saving
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Get formatted checked date
   */
  getFormattedCheckedDate(): string {
    if (!this.data.indikator.checked) return '-';
    const date = new Date(this.data.indikator.checked);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Get checked by user name
   */
  getCheckedByUserName(): string {
    // Debug: Check what we're working with
    console.log('🔍 getCheckedByUserName Debug:', {
      checked_by: this.data.indikator.checked_by,
      checked_by_type: typeof this.data.indikator.checked_by,
      checkedBy: this.data.indikator.checkedBy,
      hasCheckedBy: !!this.data.indikator.checkedBy
    });
    
    // Handle case where checked_by is an object (the full Mitarbeiter data)
    if (typeof this.data.indikator.checked_by === 'object' && this.data.indikator.checked_by !== null) {
      console.log('🔍 checked_by is an object, using it directly:', this.data.indikator.checked_by);
      
      const checkedBy = this.data.indikator.checked_by;
      
      // Try different possible property names
      let name = checkedBy.full_name || 
                 checkedBy.fullName ||
                 `${checkedBy.vorname || ''} ${checkedBy.nachname || ''}`.trim() ||
                 checkedBy.display_name ||
                 checkedBy.displayName ||
                 checkedBy.name ||
                 `User ${checkedBy.id || 'Unknown'}`;
      
      console.log('🔍 Name resolution from checked_by object:', {
        full_name: checkedBy.full_name,
        fullName: checkedBy.fullName,
        vorname: checkedBy.vorname,
        nachname: checkedBy.nachname,
        display_name: checkedBy.display_name,
        displayName: checkedBy.displayName,
        name: checkedBy.name,
        resolvedName: name,
        nameType: typeof name
      });
      
      // Ensure we always return a string
      if (typeof name !== 'string') {
        console.log('⚠️ Name is not a string, converting to string');
        name = String(name);
      }
      
      console.log('✅ Using checked_by object name:', name);
      return name;
    }
    
    // Handle superuser case (when checked_by is a number)
    if (this.data.indikator.checked_by === 999) {
      return 'System Administrator';
    }
    
    // Check if we have the checkedBy relationship loaded (fallback)
    if (this.data.indikator.checkedBy) {
      console.log('🔍 Using checkedBy relationship as fallback:', this.data.indikator.checkedBy);
      
      const checkedBy = this.data.indikator.checkedBy;
      
      // Try different possible property names
      let name = checkedBy.full_name || 
                 checkedBy.fullName ||
                 `${checkedBy.vorname || ''} ${checkedBy.nachname || ''}`.trim() ||
                 checkedBy.display_name ||
                 checkedBy.displayName ||
                 checkedBy.name ||
                 `User ${this.data.indikator.checked_by}`;
      
      // Ensure we always return a string
      if (typeof name !== 'string') {
        console.log('⚠️ Name is not a string, converting to string');
        name = String(name);
      }
      
      console.log('✅ Using checkedBy relationship name:', name);
      return name;
    }
    
    // Final fallback
    console.log('⚠️ No checkedBy data available, using fallback');
    return this.data.indikator.checked_by ? `User ${this.data.indikator.checked_by}` : '-';
  }

  /**
   * Get user name by ID (for preview purposes)
   */
  private getUserNameById(userId: number | string): string {
    // Handle superuser case
    if (userId === 999 || userId === '999') {
      return 'System Administrator';
    }
    
    // Handle case where userId might be an object (shouldn't happen, but just in case)
    if (typeof userId === 'object') {
      console.log('⚠️ getUserNameById received object instead of ID:', userId);
      return 'Unknown User';
    }
    
    // Check if this is the current user and we can get the name from session
    const session = getSession();
    console.log('🔍 getUserNameById Debug:', {
      userId: userId,
      sessionFid: session?.fid,
      sessionDienstname: session?.dienstname,
      isCurrentUser: session && session.fid == userId
    });
    
    if (session && session.fid == userId && session.dienstname) {
      console.log('✅ Using session dienstname:', session.dienstname);
      return session.dienstname;
    }
    
    // Fallback to showing the ID
    return `Mitarbeiter ${userId}`;
  }

  /**
   * Update preview information for the dialog
   */
  private updatePreviewInfo(): void {
    const session = getSession();
    const foreignId = session?.fid;
    
    console.log('🔍 Preview Info Debugging:', {
      session: session,
      userid: session?.userid,
      fid: session?.fid,
      foreignId: foreignId,
      sessionKeys: Object.keys(session || {}),
      fullSession: JSON.stringify(session, null, 2)
    });
    
    // Use fid if available, otherwise fall back to userid
    const effectiveId = foreignId || session?.userid;
    
    // Determine if this is a first save
    this.previewInfo.isFirstSave = (!this.data.indikator.checked || this.data.indikator.checked === null) || 
                                   (!this.data.indikator.checked_by || this.data.indikator.checked_by === null);
    
    if (this.previewInfo.isFirstSave) {
      // Preview what will be saved
      const now = new Date();
      this.previewInfo.willBeCheckedAt = now.toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      
      // Get user name for preview
      this.previewInfo.willBeCheckedBy = this.getUserNameById(effectiveId);
    } else {
      // Show existing saved values
      this.previewInfo.willBeCheckedAt = this.getFormattedCheckedDate();
      const checkedByName = this.getCheckedByUserName();
      console.log('🔍 Setting previewInfo.willBeCheckedBy:', {
        checkedByName: checkedByName,
        type: typeof checkedByName
      });
      this.previewInfo.willBeCheckedBy = checkedByName;
    }
  }

  onSave(): void {
    if (this.indikatorForm.valid) {
      this.loading = true;
      const formValue = this.indikatorForm.getRawValue();
      
      // Convert multi-select selections to JSON
      formValue.massnahme = JSON.stringify(this.selectedMassnahmen);
      formValue.problem = JSON.stringify(this.selectedAbweichungen);
      formValue.interne_notiz = JSON.stringify(this.selectedArtikel);
      
      // Derive befall (0/1) and befall_status_id from wert vs schwellenwert
      const wertNum = Number(formValue.wert);
      const schwellenwert = this.getSchwellenwert();
      const befall = wertNum > schwellenwert ? 1 : 0;
      let befall_status_id: '1' | '2' | '3' = '2';
      if (wertNum === 0) befall_status_id = '3';
      else if (wertNum > schwellenwert) befall_status_id = '1';
      else befall_status_id = '2';

      // Determine if this is the first save (no checked date yet OR no checked_by user yet)
      const isFirstSave = (!this.data.indikator.checked || this.data.indikator.checked === null) || 
                         (!this.data.indikator.checked_by || this.data.indikator.checked_by === null);
      console.log('🔍 Debug isFirstSave:', {
        checked: this.data.indikator.checked,
        isFirstSave: isFirstSave,
        checked_by: this.data.indikator.checked_by
      });
      
      // Update the indikator object with form values
            const updateData: any = {
              ...formValue,
              befall,
              befall_status_id,
              updated_at: new Date().toISOString() // Always set updated timestamp (this is a timestamp field, so full datetime is OK)
            };
      
            // Only set checked date and user on first save
            if (isFirstSave) {
              // Send full ISO datetime since the database column is now 'datetime' type
              updateData.checked = new Date().toISOString();
              
              // Get foreign ID from session - this is the correct ID to use
              const session = getSession();
              const foreignId = session?.fid;
              
              console.log('🔍 Session debugging:', {
                session: session,
                userid: session?.userid,        // Account ID (for reference)
                fid: session?.fid,              // Foreign ID (mitarbeiter_id or 999 for superuser)
                foreignId: foreignId,
                sessionKeys: Object.keys(session || {}),
                fullSession: JSON.stringify(session, null, 2)
              });
              
              // Check if fid is undefined or null
              if (!foreignId) {
                console.error('❌ FID is not available in session! Using fallback to userid');
                console.log('Available session keys:', Object.keys(session || {}));
                console.log('Session userid:', session?.userid);
                console.log('Session fid:', session?.fid);
              }
              
              // Set checked_by to foreign ID (this handles both regular users and superusers)
              // If fid is not available, fall back to userid (for backward compatibility)
              if (foreignId) {
                updateData.checked_by = parseInt(String(foreignId), 10);
                console.log('✅ Using FID for checked_by:', foreignId);
              } else {
                // Fallback to userid if fid is not available
                const fallbackId = session?.userid;
                updateData.checked_by = fallbackId ? parseInt(String(fallbackId), 10) : null;
                console.log('⚠️ FID not available, using userid as fallback:', fallbackId);
              }
            }
      
      Object.assign(this.data.indikator, updateData);
      
      // Create a clean payload with only the fields we want to update
      const cleanPayload: any = {
        wert: updateData.wert,
        befall: updateData.befall,
        befall_status_id: updateData.befall_status_id,
        problem: updateData.problem,
        massnahme: updateData.massnahme,
        interne_notiz: updateData.interne_notiz,
        anmerkung: updateData.anmerkung,
        updated_at: updateData.updated_at
      };
      
      // Add checked fields only on first save
      if (isFirstSave) {
        cleanPayload.checked = updateData.checked;
        cleanPayload.checked_by = updateData.checked_by;
        console.log('✅ Adding checked fields to payload:', {
          checked: updateData.checked,
          checked_by: updateData.checked_by
        });
      } else {
        console.log('❌ Not adding checked fields (not first save)');
      }
      
            // Make actual API call to save the indikator with clean payload
            console.log('🚀 Making API call to update indikator:', this.data.indikator.id);
            console.log('🚀 Payload being sent:', JSON.stringify(cleanPayload, null, 2));
            this.kontrollterminService.updateKontrollIndikator(this.data.indikator.id, cleanPayload).subscribe({
        next: (updatedIndikator) => {
          this.loading = false;
          console.log('✅ Indikator saved successfully - API Response:', {
            id: updatedIndikator.id,
            checked: updatedIndikator.checked,
            checked_by: updatedIndikator.checked_by,
            wert: updatedIndikator.wert,
            updated_at: updatedIndikator.updated_at,
            full_response: updatedIndikator
          });
          console.log('✅ Full API response object:', JSON.stringify(updatedIndikator, null, 2));
          
          // Update the local data with user information for proper display
          if (updatedIndikator.checked_by && typeof updatedIndikator.checked_by === 'number') {
            const session = getSession();
            if (session && session.fid == updatedIndikator.checked_by && session.dienstname) {
              // Create a mock checkedBy object with the user info from session
              const mockCheckedBy = {
                id: updatedIndikator.checked_by,
                full_name: session.dienstname,
                vorname: session.dienstname.split(' ')[0] || '',
                nachname: session.dienstname.split(' ').slice(1).join(' ') || '',
                display_name: session.dienstname
              };
              
              // Add the mock object as a separate property
              (updatedIndikator as any).checkedBy = mockCheckedBy;
              console.log('✅ Added checkedBy object with session data:', mockCheckedBy);
            }
          }
          
          this.dialogRef.close(updatedIndikator);
        },
            error: (error) => {
        this.loading = false;
              console.error('❌ Error saving indikator:', error);
              console.error('❌ Error status:', error.status);
              console.error('❌ Error message:', error.message);
              console.error('❌ Error body:', error.error);
              console.error('❌ Full error object:', JSON.stringify(error, null, 2));
              // Still close the dialog but with the original data
        this.dialogRef.close(this.data.indikator);
            }
      });
    }
  }

  onMassnahmenChange(selectedItems: MultiSelectItem[]): void {
    this.selectedMassnahmen = selectedItems;
  }

  onAbweichungenChange(selectedItems: MultiSelectItem[]): void {
    this.selectedAbweichungen = selectedItems;
  }

  onArtikelChange(selectedItems: MultiSelectItem[]): void {
    this.selectedArtikel = selectedItems;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getBefallColor(befall: any): string {
    if (befall === 1) return 'bg-red-100 text-red-800';
    if (befall === 0) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  }

  getBefallText(befall: any): string {
    if (befall === 1) return 'Ja';
    if (befall === 0) return 'Nein';
    return '-';
  }

  getBefallStatusColor(statusId: any): string {
    const statusMap: { [key: string]: string } = {
      '1': '#ef4444', // red
      '2': '#f59e0b', // yellow
      '3': '#10b981'  // green
    };
    return statusMap[statusId] || '#6b7280';
  }

  getBefallStatusText(statusId: any): string {
    const statusMap: { [key: string]: string } = {
      '1': 'Kritisch',
      '2': 'Warnung',
      '3': 'OK'
    };
    return statusMap[statusId] || '-';
  }
}
